/**
 * A month as a picture (amendment 26): what Hotspotter Wrapped hands over when somebody shares
 * it. Drawn on the device, like a cell's card (`share.ts`) — the server keeps nothing of a look
 * back and draws nothing for one.
 *
 * The favourite place is on it only when its owner asks for that. A month's card goes out to
 * people, and where somebody goes every Friday is not a thing to send by accident.
 */

import type { Persona, WrappedStats } from '$domain/wrapped.ts';
import { fitted, loadWordmark, rounded } from './share.ts';

const WIDTH = 1080;
const HEIGHT = 1350;
const MARGIN = 72;
const INNER = WIDTH - MARGIN * 2;

export type WrappedCard = {
  stats: WrappedStats;
  persona: Persona;
  /** "Oktober 2026". */
  monthName: string;
  /** The top attribute as shown: "😊 Nett · Ø 4,2 ★". */
  topAttribute: string | undefined;
  /** Badge emojis earned this month. */
  badges: string[];
  /** The favourite cell's name — only when its owner chose to put it on the picture. */
  place: string | undefined;
};

/** `text` broken into at most `lines` lines of `maxWidth`, the last one cut with an ellipsis. */
function wrapped(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  lines: number,
): string[] {
  const out: string[] = [];
  let current = '';
  for (const word of text.split(' ')) {
    const next = current === '' ? word : `${current} ${word}`;
    if (context.measureText(next).width <= maxWidth || current === '') current = next;
    else {
      out.push(current);
      current = word;
    }
  }
  if (current !== '') out.push(current);
  if (out.length <= lines) return out;
  const kept = out.slice(0, lines);
  kept[lines - 1] = `${kept[lines - 1]}…`;
  return kept;
}

/** A pointy-topped hexagon of radius `r` around (`x`, `y`), as the current path. */
function hexagon(context: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  context.beginPath();
  for (let corner = 0; corner < 6; corner++) {
    const angle = (Math.PI / 3) * corner - Math.PI / 2;
    context.lineTo(x + r * Math.cos(angle), y + r * Math.sin(angle));
  }
  context.closePath();
}

/**
 * Laid out top down with a running cursor, like the cell's card: the attribute, the badges and
 * the place are each optional, and the footer has room below all three together.
 */
export async function drawWrappedCard(card: WrappedCard): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const context = canvas.getContext('2d');
  if (context === null) return canvas;
  const { stats, persona } = card;

  const wash = context.createLinearGradient(0, 0, WIDTH, HEIGHT);
  wash.addColorStop(0, '#2b1055');
  wash.addColorStop(0.55, '#a4206a');
  wash.addColorStop(1, '#c2410c');
  context.fillStyle = wash;
  context.fillRect(0, 0, WIDTH, HEIGHT);

  // The app's own shape, large and faint, so the picture is Hotspotter before it is read.
  context.fillStyle = '#ffffff12';
  hexagon(context, 930, 170, 190);
  context.fill();
  hexagon(context, 1010, 470, 95);
  context.fill();
  hexagon(context, 110, 1250, 150);
  context.fill();

  let y = MARGIN;
  const logo = await loadWordmark();
  if (logo === undefined) {
    context.fillStyle = '#ffffff';
    context.font = '700 52px system-ui, sans-serif';
    context.fillText('hotspotter', MARGIN, y + 52);
    y += 90;
  } else {
    const width = 340;
    const height = (logo.naturalHeight / logo.naturalWidth) * width;
    context.drawImage(logo, MARGIN, y, width, height);
    y += height + 36;
  }

  context.fillStyle = '#ffffffcc';
  context.font = '600 44px system-ui, sans-serif';
  y += 44;
  context.fillText(`Mein ${card.monthName}`, MARGIN, y);

  // The type: the emoji large, the title beside it, the line under both.
  y += 28;
  context.font = '150px system-ui, sans-serif';
  context.fillText(persona.emoji, MARGIN - 6, y + 140);
  context.fillStyle = '#ffffff';
  fitted(context, persona.title, MARGIN + 190, y + 92, INNER - 190, 800, 68);
  y += 172;
  context.font = '400 36px system-ui, sans-serif';
  context.fillStyle = '#ffffffdd';
  for (const line of wrapped(context, persona.line, INNER, 2)) {
    y += 46;
    context.fillText(line, MARGIN, y);
  }

  // Four numbers in two rows of two.
  const tiles: [number, string][] = [
    [stats.reports, stats.reports === 1 ? 'Meldung' : 'Meldungen'],
    [stats.days, stats.days === 1 ? 'Tag unterwegs' : 'Tage unterwegs'],
    [stats.newCells, stats.newCells === 1 ? 'neue Wabe' : 'neue Waben'],
    [stats.districts, 'Viertel'],
  ];
  y += 40;
  const tileWidth = (INNER - 24) / 2;
  tiles.forEach(([value, label], index) => {
    const x = MARGIN + (index % 2) * (tileWidth + 24);
    const top = y + Math.floor(index / 2) * 182;
    context.fillStyle = '#ffffff1f';
    rounded(context, x, top, tileWidth, 160, 28);
    context.fill();
    context.fillStyle = '#ffffff';
    context.font = '800 80px system-ui, sans-serif';
    context.fillText(value.toLocaleString('de-DE'), x + 32, top + 92);
    context.fillStyle = '#ffffffcc';
    context.font = '500 32px system-ui, sans-serif';
    context.fillText(label, x + 32, top + 138);
  });
  y += 182 + 160;

  context.fillStyle = '#ffffff';
  if (card.topAttribute !== undefined) {
    y += 64;
    fitted(context, `Am meisten bewertet: ${card.topAttribute}`, MARGIN, y, INNER, 500, 38);
  }
  if (card.badges.length > 0) {
    y += 72;
    context.font = '54px system-ui, sans-serif';
    context.fillText(card.badges.slice(0, 8).join('  '), MARGIN, y);
  }
  if (card.place !== undefined) {
    y += 64;
    context.fillStyle = '#ffffffdd';
    fitted(context, `📍 Stammwabe: ${card.place}`, MARGIN, y, INNER, 400, 36);
  }

  context.fillStyle = '#ffffff99';
  context.font = '400 30px system-ui, sans-serif';
  context.fillText('Hotspotter Wrapped', MARGIN, HEIGHT - MARGIN);
  return canvas;
}
